'use client'

import Link from 'next/link'
import React from 'react'
import { ArrowLeft, ArrowRight, Info } from 'lucide-react'

import { containerClassName } from '@/components/Container'
import { PageHero } from '@/components/Hero/PageHero'
import { pageHeroPulledStartContentClassName } from '@/components/Hero/pageHeroLayout'
import { BookingSteps } from '@/components/Landing/BookingSteps'
import { LocationRoomTypesCarouselSection } from '@/components/Location/LocationRoomTypesCarouselSection'
import { PromotionLocationBadge } from '@/components/Promotions/PromotionLocationBadge'
import { PopularChoiceBanner } from '@/components/Room/PopularChoiceBanner'
import { RoomAmenitiesSection } from '@/components/Room/RoomAmenitiesSection'
import { RoomFeatureList } from '@/components/Room/RoomFeatureList'
import { RoomListingSummary } from '@/components/Room/RoomListingSummary'
import { RoomVariantTabs } from '@/components/Room/RoomDetailSections'
import { RoomGallery } from '@/components/Room/Gallery/RoomGallery'
import { RoomPromotionCard } from '@/components/Room/RoomPromotionCard'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { resolveApartmentRentLink } from '@/lib/apartments/apartmentBookingLink'
import { resolveApartmentRentDisplayCopy } from '@/lib/apartments/apartmentRentDisplayCopy'
import {
  getApartmentHeroImage,
  hasApartmentGalleryContent,
} from '@/lib/apartments/apartmentGallery'
import type { ApartmentAvailabilityItem } from '@/lib/apartments/apartmentsQuery'
import { buildPopularChoiceBannerProps } from '@/lib/apartments/popularChoiceViews'
import { formatPriceNumber } from '@/lib/pricing/formatPriceNumber'
import type { Locale } from '@/lib/i18n/config'
import { localizePath } from '@/lib/i18n/config'
import { useMessages } from '@/lib/i18n/useMessages'
import type { PromotionListItem } from '@/lib/promotions/promotionsQuery'
import { cn } from '@/utilities/ui'

const roomPromotionCarouselControlClassName =
  'static top-auto size-10 translate-none rounded-full border-[1.5px] border-white/95 bg-black/50 text-white hover:bg-black/60 hover:text-white disabled:opacity-40'

type RoomPageProps = {
  apartment: ApartmentAvailabilityItem
  locale: Locale
  locationApartments: ApartmentAvailabilityItem[]
  promotion?: PromotionListItem | PromotionListItem[] | null
  promotions?: PromotionListItem[]
  siblings: ApartmentAvailabilityItem[]
}

export function RoomPage({
  apartment,
  locale,
  locationApartments,
  promotion,
  promotions,
  siblings,
}: RoomPageProps) {
  const promotionList =
    promotions ?? (Array.isArray(promotion) ? promotion : promotion ? [promotion] : [])
  const messages = useMessages()
  const copy = messages.roomDetail
  const rentCopy = resolveApartmentRentDisplayCopy(apartment, {
    rentPrimaryText: `${copy.rentEyebrowBefore}${copy.rentEyebrowHighlight}${copy.rentEyebrowAfter}`,
    rentSecondaryText: copy.rentTermLabel,
  })
  const popularChoice = buildPopularChoiceBannerProps(apartment.id, apartment.popularChoice, copy)
  const rentLink = resolveApartmentRentLink(locale, apartment.slug, apartment.externalBookingUrl)

  const heroImage = getApartmentHeroImage(apartment.galleryViews)
  const hasGallery = hasApartmentGalleryContent(apartment.galleryViews)

  return (
    <main className="overflow-x-clip bg-cream focus:outline-none" id="main-content" tabIndex={-1}>
      <PageHero
        aria-labelledby="room-detail-heading"
        as="section"
        backdropClassName="bg-bluetext"
        backgroundSrc={heroImage?.src}
        className="overflow-hidden"
        contentClassName={pageHeroPulledStartContentClassName}
        contentWrapperClassName={cn(containerClassName, 'w-full items-start py-0 lg:py-0')}
        gradientClassName={heroImage?.src ? 'hero-overlay-image-wash' : false}
        pullUnderHeader
      >
        <Link
          className="inline-flex w-fit items-center gap-1 font-outfit text-base font-medium text-white transition-colors hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          href={localizePath('/apartments', locale)}
        >
          <ArrowLeft aria-hidden className="size-4 shrink-0" />
          {copy.backLabel}
        </Link>
        <h1
          className="text-title mt-8 text-balance leading-none tracking-[-0.04em] text-white drop-shadow-[0_16px_32px_-8px_rgba(12,12,13,0.4)]"
          id="room-detail-heading"
        >
          {apartment.name}
        </h1>
      </PageHero>

      <section aria-label={copy.detailsAriaLabel} className="overflow-x-clip bg-cream">
        <div
          className={cn(
            containerClassName,
            'py-8',
          )}
        >
          <div
            className={cn(
              'grid min-w-0 gap-5 lg:items-start lg:gap-x-16',
              hasGallery
                ? "[grid-template-areas:'badge'_'title'_'gallery'_'content'] lg:grid-cols-2 lg:[grid-template-areas:'stack_gallery']"
                : "[grid-template-areas:'badge'_'title'_'content']",
            )}
          >
          <div className="flex min-w-0 flex-col gap-5 [grid-area:stack] max-lg:contents">
            <div className="min-w-0 [grid-area:badge]">
              <PromotionLocationBadge label={apartment.location} />
            </div>

            <div className="min-w-0 [grid-area:title]">
              <p className="font-outfit text-2xl font-medium leading-7 text-dark-800">
                {apartment.name} {copy.detailsLabel}
              </p>
            </div>

            <div className="flex min-w-0 flex-col gap-5 py-3 [grid-area:content] lg:py-0">
              {apartment.description && !apartment.content ? (
                <p className="w-full text-start font-outfit text-base leading-6 text-dark-800/75">
                  {apartment.description}
                </p>
              ) : null}

              {apartment.content ? (
                <RichText
                  className="w-full text-start font-outfit text-base leading-6 text-dark-800/75 [&_p]:text-start [&_strong]:font-semibold [&_strong]:!text-dark-800/75"
                  data={apartment.content}
                  enableGutter={false}
                  enableProse={false}
                />
              ) : null}

              {siblings.length > 1 ? (
                <div>
                  <p className="mb-2 font-outfit text-base font-medium text-dark-800">
                    {copy.apartmentTypeLabel}
                  </p>
                  <RoomVariantTabs
                    activeSlug={apartment.slug}
                    copy={{ ariaLabel: copy.variantTabsAriaLabel }}
                    locale={locale}
                    siblings={siblings}
                  />
                </div>
              ) : null}

              <RoomFeatureList features={apartment.features} />

              <RoomListingSummary apartment={apartment} />

              <div
                className="flex flex-col gap-3 border-t border-black/10 pt-4 md:flex-row md:items-center md:justify-between md:gap-4"
                id="rent"
              >
                <div className="flex items-start justify-between gap-4 md:min-w-0">
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="font-outfit text-base font-medium leading-5 text-dark-800 underline decoration-wavy decoration-turquoise underline-offset-4">
                      {rentCopy.rentPrimaryText}
                    </p>
                    <p className="font-outfit text-base leading-5 text-dark-800/65">
                      {rentCopy.rentSecondaryText}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-col items-end text-right md:hidden">
                    <p className="font-outfit text-base leading-5 text-dark-800/65">
                      {copy.startingAtLabel}
                    </p>
                    <p className="font-outfit text-2xl font-medium leading-8 text-dark-800">
                      {formatPriceNumber(apartment.price, locale)} €
                    </p>
                  </div>
                </div>

                <div className="hidden shrink-0 items-center gap-6 md:flex">
                  <div className="text-right">
                    <p className="font-outfit text-base leading-5 text-dark-800/65">
                      {copy.startingAtLabel}
                    </p>
                    <p className="font-outfit text-2xl font-medium leading-8 text-dark-800">
                      {formatPriceNumber(apartment.price, locale)} €
                    </p>
                  </div>
                  <Button
                    asChild
                    className="h-12 rounded-[10px] px-4 font-outfit text-base font-medium"
                    icon={<ArrowRight aria-hidden className="size-4" />}
                    iconPosition="trailing"
                  >
                    <Link href={rentLink.href} rel="noopener noreferrer" target="_blank">
                      {copy.rentLabel}
                      <span className="sr-only"> {messages.common.opensInNewTab}</span>
                    </Link>
                  </Button>
                </div>

                <Button
                  asChild
                  className="h-12 w-full rounded-[10px] px-4 font-outfit text-base font-medium md:hidden"
                  icon={<ArrowRight aria-hidden className="size-4" />}
                  iconPosition="trailing"
                >
                  <Link href={rentLink.href} rel="noopener noreferrer" target="_blank">
                    {copy.rentLabel}
                    <span className="sr-only"> {messages.common.opensInNewTab}</span>
                  </Link>
                </Button>
              </div>

              {popularChoice ? <PopularChoiceBanner {...popularChoice} /> : null}
            </div>
          </div>

          {hasGallery ? (
            <div className="flex min-w-0 flex-col gap-2 [grid-area:gallery]">
              <div
                className={cn(
                  'relative min-w-0 overflow-hidden',
                  'min-h-[20rem] h-[20rem]',
                  'sm:min-h-[34rem] sm:h-[34rem]',
                  'lg:min-h-[40rem] lg:max-h-[44rem]',
                )}
              >
                <RoomGallery
                  carouselKey={apartment.id}
                  className="size-full"
                  views={apartment.galleryViews}
                />
              </div>
              <p className="flex items-start gap-2 font-outfit text-sm leading-5 text-dark-800/75">
                <Info aria-hidden className="mt-0.5 size-4 shrink-0 text-[#0B765A]/80" />
                {copy.gallery.layoutDisclaimer}
              </p>
            </div>
          ) : null}
          </div>

          {promotionList.length > 0 ? (
            <div className="mt-2.5 md:mt-3 lg:mt-8">
              {/* Mobile Carousel (< md) */}
              <div className="md:hidden">
                <Carousel
                  aria-label={messages.landing.bestDealPromotions.carouselAriaLabel}
                  className="w-full"
                  opts={{
                    align: 'start',
                    containScroll: 'trimSnaps',
                  }}
                >
                  <CarouselContent className="-ml-2.5 items-stretch">
                    {promotionList.map((item) => (
                      <CarouselItem
                        className={cn(
                          'flex flex-col pl-2.5',
                          promotionList.length > 1
                            ? 'basis-[84%] sm:basis-[75%]'
                            : 'basis-full',
                        )}
                        key={item.id}
                      >
                        <RoomPromotionCard
                          className="h-full"
                          idPrefix="mobile-room-promotion"
                          locale={locale}
                          promotion={item}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>

                  {promotionList.length > 1 ? (
                    <div className="mt-3 flex items-center justify-end gap-3">
                      <CarouselPrevious
                        aria-label={messages.landing.bestDealPromotions.carouselPrevious}
                        className={roomPromotionCarouselControlClassName}
                        size="icon"
                        variant="outline"
                      />
                      <CarouselNext
                        aria-label={messages.landing.bestDealPromotions.carouselNext}
                        className={roomPromotionCarouselControlClassName}
                        size="icon"
                        variant="outline"
                      />
                    </div>
                  ) : null}
                </Carousel>
              </div>

              {/* Desktop Grid (>= md) */}
              <ul
                aria-label={messages.landing.bestDealPromotions.carouselAriaLabel}
                className={cn(
                  'm-0 hidden list-none p-0 md:grid md:auto-rows-fr md:gap-3 lg:gap-8',
                  promotionList.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-2',
                )}
              >
                {promotionList.map((item) => (
                  <li className="flex min-w-0 flex-col" key={item.id}>
                    <RoomPromotionCard
                      className="h-full"
                      idPrefix="desktop-room-promotion"
                      locale={locale}
                      promotion={item}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </section>

      <LocationRoomTypesCarouselSection
        apartments={locationApartments}
        excludeRoomType={apartment.roomType}
        hasPromotion={promotionList.length > 0}
      />
      <RoomAmenitiesSection amenities={apartment.amenityDetails ?? []} />
      <BookingSteps />
    </main>
  )
}
